/**
 * Pending Retags tab — the primary manual-review workflow.
 *
 * Surfaces low-confidence retag proposals stashed by the repair agent
 * (research_sources rows where retag_agent_run_id IS NOT NULL AND
 * retagged_at IS NULL). Split into Academic vs Practitioner sub-tabs
 * per Paul's 2026-04-28 request. NK-CAT-academic rows (small overlap
 * subset where knowledge_type='research' but created via NK-CAT) are
 * flagged with an explicit chip.
 *
 * Per-row workflow:
 *   - Review confidence score + agent reasoning
 *   - See side-by-side: Before (snapshot) → After (agent's proposal)
 *   - Approve   → set retagged_at = NOW()
 *   - Reject    → restore from snapshot
 *   - Edit      → modify tags inline before commit
 *
 * Batch:
 *   - Multi-select checkboxes → "Approve Selected" button
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, ChevronRight, Edit3, Pencil,
  ThumbsDown, ThumbsUp, X,
} from 'lucide-react';
import { adminFetch } from '../../../utils/adminApi';
import type { PendingRetagItem, PendingRetagsResponse } from './types';

const PAGE_SIZE = 25;

const TAG_DIMENSIONS: Array<{ key: keyof PendingRetagItem; label: string }> = [
  { key: 'demographic_contexts', label: 'Demographics' },
  { key: 'sex_hormone_contexts', label: 'Sex / Hormone' },
  { key: 'condition_contexts', label: 'Conditions' },
  { key: 'goal_contexts', label: 'Goals' },
  { key: 'sport_contexts', label: 'Sports' },
  { key: 'intervention_contexts', label: 'Interventions' },
  { key: 'lens_contexts', label: 'Lenses' },
  { key: 'phase_contexts', label: 'Phases' },
  { key: 'mechanism_contexts', label: 'Mechanisms' },
  { key: 'nutrition_contexts', label: 'Nutrition' },
  { key: 'sleep_contexts', label: 'Sleep' },
  { key: 'recovery_contexts', label: 'Recovery' },
  { key: 'psychological_contexts', label: 'Psychological' },
  { key: 'environmental_contexts', label: 'Environmental' },
  { key: 'hydration_contexts', label: 'Hydration' },
  { key: 'applies_to_profiles', label: 'Applies To' },
  { key: 'not_applicable_to_profiles', label: 'Excluded' },
];

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((x) => setB.has(x));
}

function tagDiff(oldTags: string[], newTags: string[]) {
  const oldSet = new Set(oldTags);
  const newSet = new Set(newTags);
  const added = newTags.filter((t) => !oldSet.has(t));
  const removed = oldTags.filter((t) => !newSet.has(t));
  const same = newTags.filter((t) => oldSet.has(t));
  return { added, removed, same };
}

function ConfidenceBadge({ score }: { score: number | null | undefined }) {
  if (score == null) return <span className="text-xs text-gray-400">—</span>;
  const tone = score >= 0.85 ? 'bg-emerald-100 text-emerald-800'
             : score >= 0.70 ? 'bg-amber-100 text-amber-800'
             : 'bg-rose-100 text-rose-800';
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
      {(score * 100).toFixed(0)}%
    </span>
  );
}

function RetagDetailPanel({
  item,
  onClose,
  onActionDone,
}: {
  item: PendingRetagItem;
  onClose: () => void;
  onActionDone: () => void;
}) {
  const [notes, setNotes] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedTags, setEditedTags] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    TAG_DIMENSIONS.forEach(({ key }) => {
      const v = item[key as keyof PendingRetagItem];
      init[key as string] = Array.isArray(v) ? [...(v as string[])] : [];
    });
    return init;
  });

  const approve = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/researcher-knowledge/pending-retags/${item.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes || null }),
      }),
    onSuccess: () => { onActionDone(); onClose(); },
  });

  const reject = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/researcher-knowledge/pending-retags/${item.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes || null }),
      }),
    onSuccess: () => { onActionDone(); onClose(); },
  });

  const editAndCommit = useMutation({
    mutationFn: () =>
      adminFetch(`/api/admin/researcher-knowledge/pending-retags/${item.id}/edit`, {
        method: 'POST',
        body: JSON.stringify({ ...editedTags, notes: notes || null }),
      }),
    onSuccess: () => { onActionDone(); onClose(); },
  });

  const overallConfidence = item.retag_confidence_scores?.overall;
  const snapshot = item.original_tags_snapshot || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="sticky top-0 flex items-start justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div className="pr-8">
            <h2 className="text-lg font-semibold text-gray-900">{item.title}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>{item.journal} · {item.publication_year}</span>
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-700">{item.knowledge_type}</span>
              {item.is_nkcat_academic && (
                <span className="rounded bg-purple-100 px-1.5 py-0.5 text-purple-800">
                  NK-CAT academic
                </span>
              )}
              <span>Confidence:</span>
              <ConfidenceBadge score={overallConfidence} />
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4 p-6">
          {/* Reasoning */}
          {item.retag_reasoning && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
              <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">
                Agent Reasoning
              </h4>
              <p className="whitespace-pre-wrap text-sm text-gray-700">{item.retag_reasoning}</p>
            </div>
          )}

          {/* Per-dimension confidence (if present) */}
          {item.retag_confidence_scores &&
           Object.keys(item.retag_confidence_scores).length > 1 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase text-gray-500">
                Per-Dimension Confidence
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
                {Object.entries(item.retag_confidence_scores).map(([dim, score]) => (
                  <div key={dim} className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 px-2 py-1">
                    <span className="text-gray-700">{dim}</span>
                    <ConfidenceBadge score={score as number} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Side-by-side tag diff */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase text-gray-500">
                Tag Changes (Original → Proposed)
              </h4>
              <button
                onClick={() => setEditMode(!editMode)}
                className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50"
              >
                <Edit3 className="h-3 w-3" /> {editMode ? 'Cancel edit' : 'Edit before commit'}
              </button>
            </div>
            <div className="space-y-2 text-sm">
              {TAG_DIMENSIONS.map(({ key, label }) => {
                const proposed = (item[key as keyof PendingRetagItem] as string[]) || [];
                const original = (snapshot as Record<string, string[]>)[key as string] || [];
                const isChanged = !arraysEqual(original, proposed);
                if (!isChanged && proposed.length === 0) return null;
                const { added, removed, same } = tagDiff(original, proposed);
                return (
                  <div key={key as string} className={`rounded border p-2 ${isChanged ? 'border-blue-200 bg-blue-50/30' : 'border-gray-100 bg-white'}`}>
                    <div className="mb-1 text-xs font-medium text-gray-600">{label}</div>
                    {editMode ? (
                      <input
                        type="text"
                        value={(editedTags[key as string] || []).join(', ')}
                        onChange={(e) =>
                          setEditedTags({
                            ...editedTags,
                            [key as string]: e.target.value
                              .split(',')
                              .map((s) => s.trim())
                              .filter(Boolean),
                          })
                        }
                        className="w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono"
                        placeholder="comma-separated tags"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {same.map((t) => (
                          <span key={`s-${t}`} className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                            {t}
                          </span>
                        ))}
                        {added.map((t) => (
                          <span key={`a-${t}`} className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
                            + {t}
                          </span>
                        ))}
                        {removed.map((t) => (
                          <span key={`r-${t}`} className="rounded bg-rose-100 px-1.5 py-0.5 text-xs text-rose-800 line-through">
                            − {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-gray-600">Notes (optional, audit-logged)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              placeholder="e.g. agent confused chronic with acute — manually corrected"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4">
            {editMode ? (
              <button
                onClick={() => editAndCommit.mutate()}
                disabled={editAndCommit.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                <Pencil className="h-4 w-4" /> Save Edits & Commit
              </button>
            ) : (
              <>
                <button
                  onClick={() => approve.mutate()}
                  disabled={approve.isPending}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  <ThumbsUp className="h-4 w-4" /> Approve as proposed
                </button>
                <button
                  onClick={() => reject.mutate()}
                  disabled={reject.isPending}
                  className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                >
                  <ThumbsDown className="h-4 w-4" /> Reject (restore original)
                </button>
              </>
            )}
            {(approve.isError || reject.isError || editAndCommit.isError) && (
              <span className="text-sm text-rose-600">
                Action failed — try again or check console.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PendingRetagsTab() {
  const queryClient = useQueryClient();
  const [type, setType] = useState<'academic' | 'practitioner'>('academic');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<'confidence_asc' | 'recency'>('confidence_asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openItem, setOpenItem] = useState<PendingRetagItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['rk', 'pending-retags', type, page, sortBy],
    queryFn: () =>
      adminFetch<PendingRetagsResponse>(
        `/api/admin/researcher-knowledge/pending-retags?type=${type}&sort_by=${sortBy}&page=${page}&page_size=${PAGE_SIZE}`
      ),
    placeholderData: (prev) => prev,
  });

  const batchApprove = useMutation({
    mutationFn: (rowIds: string[]) =>
      adminFetch<{ approved: number; skipped: number; requested: number }>(
        '/api/admin/researcher-knowledge/pending-retags/batch-approve',
        {
          method: 'POST',
          body: JSON.stringify({ row_ids: rowIds, notes: null }),
        }
      ),
    onSuccess: () => {
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['rk', 'pending-retags'] });
      queryClient.invalidateQueries({ queryKey: ['rk', 'stats'] });
    },
  });

  const allOnPageIds = useMemo(() => (data?.items || []).map((i) => i.id), [data]);
  const allSelected = allOnPageIds.length > 0 && allOnPageIds.every((id) => selectedIds.has(id));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['rk', 'pending-retags'] });
    queryClient.invalidateQueries({ queryKey: ['rk', 'stats'] });
  };

  return (
    <div className="space-y-4">
      {/* Sub-tab: Academic vs Practitioner */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => { setType('academic'); setPage(1); setSelectedIds(new Set()); }}
          className={`relative -mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            type === 'academic' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Academic Research
        </button>
        <button
          onClick={() => { setType('practitioner'); setPage(1); setSelectedIds(new Set()); }}
          className={`relative -mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
            type === 'practitioner' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Practitioner (NK-CAT)
        </button>
      </div>

      {/* Filter / batch action bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
          <option value="confidence_asc">Lowest confidence first</option>
          <option value="recency">Most recent first</option>
        </select>
        {selectedIds.size > 0 && (
          <>
            <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
            <button
              onClick={() => batchApprove.mutate(Array.from(selectedIds))}
              disabled={batchApprove.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle2 className="h-4 w-4" />
              Approve Selected
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50"
            >
              Clear
            </button>
          </>
        )}
        <div className="ml-auto text-sm text-gray-600">
          {data ? `${data.total.toLocaleString()} pending in ${type}` : '—'}
        </div>
      </div>

      {batchApprove.isSuccess && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Batch approved {batchApprove.data?.approved} / {batchApprove.data?.requested}
          {batchApprove.data?.skipped ? ` (${batchApprove.data.skipped} skipped — already-approved or invalid)` : ''}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(new Set([...selectedIds, ...allOnPageIds]));
                    } else {
                      const next = new Set(selectedIds);
                      allOnPageIds.forEach((id) => next.delete(id));
                      setSelectedIds(next);
                    }
                  }}
                />
              </th>
              <th className="px-3 py-2 text-left">Title</th>
              <th className="px-3 py-2 text-left">Confidence</th>
              <th className="px-3 py-2 text-left">Quick Diff</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={5} className="p-8 text-center text-gray-500">Loading…</td></tr>
            )}
            {data?.items.map((row) => {
              const proposedConditions = row.condition_contexts || [];
              const originalConditions = (row.original_tags_snapshot?.condition_contexts as string[]) || [];
              const condDiff = tagDiff(originalConditions, proposedConditions);
              return (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={(e) => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) next.add(row.id);
                        else next.delete(row.id);
                        setSelectedIds(next);
                      }}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 line-clamp-2">{row.title}</div>
                    <div className="text-xs text-gray-500">
                      {row.journal} · {row.publication_year}
                      {row.is_nkcat_academic && (
                        <span className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-purple-800">NK-CAT academic</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <ConfidenceBadge score={row.retag_confidence_scores?.overall} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xs text-gray-600">
                      {condDiff.added.length === 0 && condDiff.removed.length === 0 ? (
                        <span className="text-gray-400">conditions unchanged</span>
                      ) : (
                        <>
                          {condDiff.added.length > 0 && (
                            <span className="mr-2 text-emerald-700">+{condDiff.added.length}</span>
                          )}
                          {condDiff.removed.length > 0 && (
                            <span className="text-rose-700">−{condDiff.removed.length}</span>
                          )}
                          <span className="ml-1 text-gray-400">conditions</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setOpenItem(row)}
                      className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs hover:bg-gray-100"
                    >
                      Review <ChevronRight className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              );
            })}
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-gray-500">
                  No pending {type} retags. The queue is clear.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            {(data.page - 1) * data.page_size + 1}–
            {Math.min(data.page * data.page_size, data.total)} of {data.total.toLocaleString()}
          </div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-2 py-1">Page {data.page} of {data.total_pages}</span>
            <button
              disabled={page >= data.total_pages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {openItem && (
        <RetagDetailPanel
          item={openItem}
          onClose={() => setOpenItem(null)}
          onActionDone={refresh}
        />
      )}
    </div>
  );
}
